import { CandidateLayout } from '@/components/layout/CandidateLayout';
import { StatCard } from '@/components/dashboard/StatCard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  Briefcase,
  CheckCircle2,
  Clock,
  XCircle,
  Calendar,
  FileText,
  ArrowRight,
  Bell,
} from 'lucide-react';

const CandidateDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Mock data - in production, fetch from API
  const stats = {
    applied: 12,
    shortlisted: 3,
    interviews: 2,
    rejected: 4,
  };

  const recentNotifications = [
    {
      id: '1',
      title: 'Shortlisted for Frontend Developer',
      company: 'TechCorp Inc.',
      time: '2 hours ago',
      type: 'shortlisted',
    },
    {
      id: '2',
      title: 'Interview Scheduled',
      company: 'StartupXYZ',
      time: '1 day ago',
      type: 'interview',
    },
    {
      id: '3',
      title: 'New Task Assigned',
      company: 'InnovateTech',
      time: '3 days ago',
      type: 'task',
    },
  ];

  const upcomingInterviews = [
    {
      id: '1',
      company: 'TechCorp Inc.',
      position: 'Senior Frontend Developer',
      date: '2024-01-28',
      time: '10:00 AM',
      type: 'Video Call',
    },
  ];

  return (
    <CandidateLayout>
      <div className="space-y-6">
        {/* Welcome Header */}
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">
            Welcome back, {user?.name?.split(' ')[0] || 'Candidate'}!
          </h1>
          <p className="text-muted-foreground">
            Here's an overview of your job applications.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
          <StatCard
            title="Applied"
            value={stats.applied}
            icon={<Briefcase className="h-5 w-5 sm:h-6 sm:w-6" />}
            variant="primary"
          />
          <StatCard
            title="Shortlisted"
            value={stats.shortlisted}
            icon={<CheckCircle2 className="h-5 w-5 sm:h-6 sm:w-6" />}
            variant="accent"
          />
          <StatCard
            title="Interviews"
            value={stats.interviews}
            icon={<Calendar className="h-5 w-5 sm:h-6 sm:w-6" />}
            variant="default"
          />
          <StatCard
            title="Rejected"
            value={stats.rejected}
            icon={<XCircle className="h-5 w-5 sm:h-6 sm:w-6" />}
            variant="destructive"
          />
        </div>

        {/* Main Grid */}
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Profile Completion Card */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg">Complete Your Profile</CardTitle>
              <Badge variant="secondary">60% Complete</Badge>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="h-2 rounded-full bg-muted">
                <div className="h-2 w-3/5 rounded-full bg-primary" />
              </div>
              <p className="text-sm text-muted-foreground">
                A complete profile increases your chances of getting hired by 3x.
              </p>
              <Button
                onClick={() => navigate('/candidate/profile')}
                className="w-full sm:w-auto"
              >
                <FileText className="mr-2 h-4 w-4" />
                Update Profile
              </Button>
            </CardContent>
          </Card>

          {/* Upcoming Interviews */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg">Upcoming Interviews</CardTitle>
              <Button variant="link" className="text-primary p-0">
                View All <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent>
              {upcomingInterviews.length > 0 ? (
                <div className="space-y-3">
                  {upcomingInterviews.map((interview) => (
                    <div
                      key={interview.id}
                      className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 rounded-lg border border-border p-3"
                    >
                      <div>
                        <p className="font-medium">{interview.company}</p>
                        <p className="text-sm text-muted-foreground">
                          {interview.position}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Badge variant="outline">{interview.type}</Badge>
                        <span className="text-muted-foreground">
                          {new Date(interview.date).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                          })}{' '}
                          at {interview.time}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <Clock className="h-12 w-12 text-muted-foreground/50 mb-2" />
                  <p className="text-muted-foreground">No upcoming interviews</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent Notifications */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg">Recent Notifications</CardTitle>
            <Button
              variant="link"
              className="text-primary p-0"
              onClick={() => navigate('/candidate/notifications')}
            >
              View All <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className="flex items-start gap-3 rounded-lg border border-border p-3"
                >
                  <div
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
                      notification.type === 'shortlisted'
                        ? 'bg-primary/10 text-primary'
                        : notification.type === 'interview'
                        ? 'bg-accent/10 text-accent'
                        : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    <Bell className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{notification.title}</p>
                    <p className="text-sm text-muted-foreground">
                      {notification.company}
                    </p>
                  </div>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {notification.time}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </CandidateLayout>
  );
};

export default CandidateDashboard;
